import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import api, { getFullImageUrl } from "../../services/api";
import { uploadImageToCloudinary } from "../../services/cloudinary";

const AdminBanners = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const fileInputRef = React.useRef(null);

  const bannerKeys = [
    { key: "home-slider", label: "Home Main Slider (Multiple allowed)" },
    { key: "mega-sale", label: "Mega Sale Banner (Top Wide)" },
    { key: "banner-km", label: "Middle Banner (Simple Image)" },
    { key: "promo", label: "Technology Banner (Bước đi tương lai)" },
    { key: "home-promo-1", label: "Home Promo 1 (Spring Sale Position)" },
    { key: "home-promo-2", label: "Home Promo 2 (Summer Sale Position)" },
  ];

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    try {
      setLoading(true);
      const res = await api.get("/admin/banners");
      setBanners(res.data);
    } catch (error) {
      console.error("❌ Error fetching banners:", error);
      toast.error("Không thể tải danh sách banners");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (banner) => {
    setEditingBanner({ ...banner });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      
      // Ensure we have a key and image
      if (!editingBanner.key || !editingBanner.image) {
        toast.error("Vui lòng nhập đầy đủ vị trí và ảnh banner");
        setLoading(false);
        return;
      }

      if (editingBanner._id) {
        await api.put(`/admin/banners/${editingBanner._id}`, editingBanner);
        toast.success("Cập nhật banner thành công!");
      } else {
        await api.post("/admin/banners", editingBanner);
        toast.success("Tạo banner mới thành công!");
      }
      setShowModal(false);
      fetchBanners();
    } catch (error) {
      console.error("❌ Save error:", error);
      toast.error("Lỗi khi lưu banner");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error("Dung lượng ảnh tối đa là 10MB");
      return;
    }

    const formData = new FormData();
    formData.append("image", file);

    setUploading(true);
    try {
      const imageUrl = await uploadImageToCloudinary(file, "tahashoes_banners");
      if (imageUrl) {
        setEditingBanner({ ...editingBanner, image: imageUrl });
        toast.success("Tải ảnh lên Cloudinary thành công!");
      }
    } catch (error) {
      console.error("❌ Cloudinary upload error:", error);
      toast.error("Không thể tải ảnh lên Cloudinary");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bạn có chắc chắn muốn xóa banner này?")) return;
    try {
      setLoading(true);
      await api.delete(`/admin/banners/${id}`);
      toast.success("Xóa banner thành công!");
      fetchBanners();
    } catch (error) {
      console.error("❌ Delete error:", error);
      toast.error("Lỗi khi xóa banner");
    } finally {
      setLoading(false);
    }
  };

  const addNewBanner = () => {
    setEditingBanner({
      key: "mega-sale",
      image: "",
      title: "",
      subtitle: "",
      highlight: "",
      description: "",
      link: "/products",
      buttonText: "Mua Ngay",
      isActive: true,
      theme: "dark",
    });
    setShowModal(true);
  };

  if (loading && banners.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent shadow-xl"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-8 bg-gray-50 min-h-screen font-inter">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-slate-800 tracking-tight">Quản lý Banner</h1>
          <p className="text-sm text-slate-400 font-bold uppercase tracking-widest mt-1">Cài đặt các banner quảng cáo trên trang chủ</p>
        </div>
        <button
          onClick={addNewBanner}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-2xl font-black text-sm uppercase tracking-widest transition-all shadow-xl shadow-blue-500/20 active:scale-95"
        >
          <span className="material-symbols-outlined">add</span>
          Thêm Banner mới
        </button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {banners.map((banner) => (
          <div key={banner._id} className="bg-white rounded-[32px] shadow-sm border border-gray-100 overflow-hidden hover:shadow-xl transition-all group flex flex-col md:flex-row">
            <div className="w-full md:w-1/3 aspect-video md:aspect-auto relative bg-slate-100 overflow-hidden">
              {banner.image ? (
                <img src={banner.image} alt={banner.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-300 italic text-xs">Không có ảnh</div>
              )}
              <div className="absolute top-4 left-4">
                <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider backdrop-blur-md shadow-lg ${banner.isActive ? 'bg-green-500/80 text-white' : 'bg-red-500/80 text-white'}`}>
                  {banner.isActive ? 'Đang bật' : 'Đang tắt'}
                </span>
              </div>
            </div>
            
            <div className="flex-1 p-6 flex flex-col justify-between">
              <div>
                <div className="flex justify-between items-start mb-2">
                  <span className="text-[10px] bg-slate-100 text-slate-500 font-black px-2 py-1 rounded uppercase tracking-tighter">
                    {banner.key}
                  </span>
                  <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => handleEdit(banner)} className="w-8 h-8 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center hover:bg-blue-600 hover:text-white transition-all">
                      <span className="material-symbols-outlined text-[18px]">edit</span>
                    </button>
                    <button onClick={() => handleDelete(banner._id)} className="w-8 h-8 rounded-full bg-red-50 text-red-600 flex items-center justify-center hover:bg-red-600 hover:text-white transition-all">
                      <span className="material-symbols-outlined text-[18px]">delete</span>
                    </button>
                  </div>
                </div>
                <h3 className="text-xl font-black text-slate-800 mb-1 leading-tight">{banner.title || "Không có tiêu đề"}</h3>
                <p className="text-blue-600 font-bold text-xs uppercase tracking-widest">{banner.subtitle}</p>
                <div className="mt-4 space-y-1">
                  <p className="text-xs text-slate-400 line-clamp-2 italic">{banner.description}</p>
                  <p className="text-[10px] text-slate-500 font-mono truncate">URL: {banner.link}</p>
                </div>
              </div>
              
              <div className="mt-6 pt-6 border-t border-slate-50 flex items-center justify-between">
                <button
                  onClick={() => handleEdit(banner)}
                  className="px-4 py-2 bg-slate-900 text-white rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-black transition-all"
                >
                  Chi tiết
                </button>
                <div className="flex gap-1">
                   {banner.theme && (
                     <span className={`w-3 h-3 rounded-full border border-slate-200 ${banner.theme === 'dark' ? 'bg-slate-900' : 'bg-white'}`} title={`Theme: ${banner.theme}`}></span>
                   )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showModal && editingBanner && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="bg-white w-full max-w-4xl max-h-[90vh] rounded-[40px] overflow-hidden shadow-2xl flex flex-col animate-modalUp">
            <div className="p-6 bg-slate-900 text-white flex justify-between items-center">
              <div>
                <h3 className="font-black uppercase tracking-widest">
                  {editingBanner._id ? "Chỉnh sửa Banner" : "Thêm Banner mới"}
                </h3>
                <p className="text-[10px] text-slate-400 font-bold tracking-tighter">ID: {editingBanner._id || "NEW"}</p>
              </div>
              <button onClick={() => setShowModal(false)} className="w-10 h-10 rounded-full hover:bg-slate-800 flex items-center justify-center transition-all bg-slate-800 shadow-inner">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-8 space-y-8 no-scrollbar bg-white">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Left Column */}
                <div className="space-y-6">
                  <div>
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 px-1">Vị trí hiển thị (Key)</label>
                    <select
                      value={editingBanner.key}
                      onChange={(e) => setEditingBanner({ ...editingBanner, key: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:ring-4 focus:ring-blue-100 outline-none font-bold text-sm transition-all"
                    >
                      {bannerKeys.map(k => <option key={k.key} value={k.key}>{k.label}</option>)}
                      <option value="custom">Tùy chỉnh khác</option>
                    </select>
                  </div>

                  {editingBanner.key === "custom" && (
                     <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 px-1">Tên Key tự định nghĩa</label>
                        <input
                          type="text"
                          placeholder="Ví dụ: flash-sale-banner"
                          onChange={(e) => setEditingBanner({ ...editingBanner, key: e.target.value })}
                          className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:ring-4 focus:ring-blue-100 outline-none font-bold text-sm"
                        />
                     </div>
                  )}

                  <div>
                    <div className="flex justify-between items-center mb-2 px-1">
                      <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest">Ảnh Banner (URL)</label>
                      <button 
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="text-[10px] font-black text-blue-600 hover:text-blue-700 uppercase tracking-widest flex items-center gap-1 disabled:opacity-50"
                      >
                        <span className="material-symbols-outlined text-[14px]">upload_file</span>
                        {uploading ? "Đang tải..." : "Tải lên từ máy"}
                      </button>
                      <input 
                        type="file" 
                        ref={fileInputRef} 
                        onChange={handleFileUpload} 
                        className="hidden" 
                        accept="image/*"
                      />
                    </div>
                    <input
                      type="text"
                      placeholder="https://example.com/image.png"
                      value={editingBanner.image}
                      onChange={(e) => setEditingBanner({ ...editingBanner, image: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:ring-4 focus:ring-blue-100 outline-none font-bold text-sm transition-all mb-4"
                    />
                    <div className="w-full aspect-video rounded-3xl bg-slate-50 border-2 border-dashed border-slate-200 overflow-hidden flex items-center justify-center relative group">
                       {editingBanner.image ? (
                         <img 
                           src={getFullImageUrl(editingBanner.image)} 
                           alt="Preview" 
                           className="w-full h-full object-cover transition-transform group-hover:scale-110" 
                         />
                       ) : (
                         <span className="text-slate-300 text-xs font-bold uppercase tracking-widest">Xem trước ảnh</span>
                       )}
                       {uploading && (
                         <div className="absolute inset-0 bg-white/60 backdrop-blur-sm flex items-center justify-center">
                            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                         </div>
                       )}
                    </div>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-6">
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 px-1">Tiêu đề chính</label>
                      <input
                        type="text"
                        value={editingBanner.title}
                        onChange={(e) => setEditingBanner({ ...editingBanner, title: e.target.value })}
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:ring-4 focus:ring-blue-100 outline-none font-bold text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 px-1">Tiêu đề phụ (Subtitle)</label>
                      <input
                        type="text"
                        value={editingBanner.subtitle}
                        onChange={(e) => setEditingBanner({ ...editingBanner, subtitle: e.target.value })}
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:ring-4 focus:ring-blue-100 outline-none font-bold text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 px-1">Văn bản nổi bật (Highlight)</label>
                    <input
                      type="text"
                      value={editingBanner.highlight}
                      onChange={(e) => setEditingBanner({ ...editingBanner, highlight: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:ring-4 focus:ring-blue-100 outline-none font-bold text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 px-1">Mô tả ngắn</label>
                    <textarea
                      rows="3"
                      value={editingBanner.description}
                      onChange={(e) => setEditingBanner({ ...editingBanner, description: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:ring-4 focus:ring-blue-100 outline-none font-bold text-sm resize-none"
                    ></textarea>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 px-1">Đường dẫn (Link)</label>
                      <input
                        type="text"
                        value={editingBanner.link}
                        onChange={(e) => setEditingBanner({ ...editingBanner, link: e.target.value })}
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:ring-4 focus:ring-blue-100 outline-none font-bold text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 px-1">Nút bấm (CTA)</label>
                      <input
                        type="text"
                        value={editingBanner.buttonText}
                        onChange={(e) => setEditingBanner({ ...editingBanner, buttonText: e.target.value })}
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:ring-4 focus:ring-blue-100 outline-none font-bold text-sm"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-[28px] border border-slate-100">
                    <div className="flex items-center gap-3">
                      <span className="text-[10px] font-black text-slate-700 uppercase tracking-widest px-1">Giao diện:</span>
                      <div className="flex gap-2">
                        {['light', 'dark'].map(t => (
                          <button
                            key={t}
                            onClick={() => setEditingBanner({ ...editingBanner, theme: t })}
                            className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-tight transition-all ${editingBanner.theme === t ? 'bg-slate-900 text-white' : 'bg-white text-slate-400 border border-slate-100'}`}
                          >
                            {t}
                          </button>
                        ))}
                      </div>
                    </div>
                    <label className="flex items-center gap-3 cursor-pointer group">
                      <span className="text-[10px] font-black text-slate-700 uppercase tracking-widest">Kích hoạt:</span>
                      <div className="relative inline-flex items-center">
                        <input 
                          type="checkbox" 
                          checked={editingBanner.isActive}
                          onChange={(e) => setEditingBanner({...editingBanner, isActive: e.target.checked})}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 shadow-inner"></div>
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-8 bg-gray-50 border-t border-slate-100 flex gap-4">
               <button onClick={() => setShowModal(false)} className="flex-1 py-4 font-black text-xs text-slate-400 uppercase tracking-widest hover:text-slate-600 transition-colors">Hủy</button>
               <button 
                 onClick={handleSave} 
                 disabled={loading}
                 className="flex-[2] py-4 bg-slate-900 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-black transition-all shadow-2xl shadow-slate-300 active:scale-[0.98]"
               >
                 {loading ? 'Đang xử lý...' : 'Lưu Banner'}
               </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBanners;
